/**
 * v3.15-1167 — Platform Tour ("Inside Prestigious Hub").
 *
 * URL: /platform-tour    Heading: Inside Prestigious Hub.
 *
 * Show, don't tell. Real Hub screenshots dominate the page; each section
 * answers "How does this help me as a partner?" The goal is for a
 * prospective partner to finish this page believing they are looking at a
 * mature, enterprise-grade financial operations platform.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, LayoutDashboard, Users, MessageSquare } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';

/**
 * A single "tour stop". Screenshot dominates (large, edge-aligned); copy is
 * tight and outcome-led ("So that you …"). Sections without real screenshots
 * have been intentionally removed per v3.15-1168 — quality over quantity.
 */
const TourStop = ({ index, Icon, eyebrow, title, body, outcome, image, alt, reverse = false, testId }) => (
  <section
    className={`px-6 py-24 lg:py-32 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
    data-testid={testId}
  >
    <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
      <div className="lg:col-span-5">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 inline-flex items-center gap-2">
          <Icon className="h-4 w-4" /> {eyebrow}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.1] mb-5">
          {title}
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed mb-6">{body}</p>
        <div className="border-l-4 border-gold pl-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gold/90 mb-1">Partner Outcome</p>
          <p className="text-sm text-slate-800 font-medium leading-relaxed">{outcome}</p>
        </div>
      </div>
      <div className="lg:col-span-7">
        <figure className="rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden bg-white">
          <img src={image} alt={alt} className="w-full h-auto block" loading="lazy" />
          <figcaption className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 border-t border-slate-100">
            Inside Prestigious Hub · Live screenshot
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

const STOPS = [
  {
    Icon: LayoutDashboard,
    eyebrow: 'Partner Dashboard',
    title: 'Every deal. Every dollar. One screen.',
    body: 'The partner dashboard is the operational hub of your business inside Prestigious Hub — pipeline value, active stages, funding events, and real-time activity, refreshed live.',
    outcome: 'You walk into Monday already knowing what is at stake and where to push — no status calls required.',
    image: '/marketing/hub-dashboard.jpg',
    alt: 'Prestigious Hub — Partner Dashboard',
    testId: 'tour-dashboard',
  },
  {
    Icon: Users,
    eyebrow: 'Client Portfolio',
    title: 'Every client you have ever referred. One workspace.',
    body: 'Stage, lender activity, document checklist, communication thread — every client file, end-to-end, in one place. Click any client to see the full file, the full conversation, the full history.',
    outcome: 'You stop hunting through email threads. The history of every relationship lives where the deal lives.',
    image: '/marketing/hub-clients.jpg',
    alt: 'Prestigious Hub — Client Portfolio',
    reverse: true,
    testId: 'tour-clients',
  },
  {
    Icon: MessageSquare,
    eyebrow: 'Communications Center',
    title: 'Talk to your Concierge from inside the deal.',
    body: 'One thread per file — pinned to the client, visible to you and your Application Concierge. Nothing buried in email, nothing lost in voicemail.',
    outcome: 'Your team and our team communicate in the same place the work lives. No context ever falls through the cracks.',
    image: '/marketing/hub-activity.jpg',
    alt: 'Prestigious Hub — Communications Center',
    testId: 'tour-communications',
  },
];

const PlatformTourPage = () => (
  <PublicLayout testId="platform-tour-page">
    {/* HERO */}
    <section className="bg-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Platform Tour</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
          Inside<br />
          <span className="text-gradient-gold">Prestigious Hub.</span>
        </h1>
        <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-2xl leading-relaxed">
          A walk-through of the financial operations platform our partners run their business on. Real screens. Real workflows. Real outcomes.
        </p>
        <p className="mt-8 text-sm text-gold/90">
          One platform. One team. Three financial solutions — Consumer Funding (EasyQuickFund), Business Funding, and Credit Improvement (CreditForward360).
        </p>
      </div>
    </section>

    {/* Tour stops */}
    {STOPS.map((stop, i) => (
      <TourStop key={stop.testId} index={i} {...stop} />
    ))}

    {/* Closing */}
    <section className="bg-gradient-to-b from-slate-900 to-primary text-white px-6 py-28 lg:py-36">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">What you just saw</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
          A mature, enterprise-grade<br />
          <span className="text-gradient-gold">financial operations platform.</span>
        </h2>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mb-4">
          This is what your partner experience looks like from day one. Not a portal. Not a form. An operating system for your funding business.
        </p>
        <p className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-10">
          More partner-facing views are added as the platform expands.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/presentation"
            className="inline-flex items-center gap-2 bg-gradient-gold text-slate-900 font-semibold px-6 py-3 rounded hover:shadow-gold transition-all"
            data-testid="platform-tour-cta-presentation"
          >
            View the Partner Presentation <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/ghost"
            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-6 py-3 rounded hover:bg-white/20 transition-all"
            data-testid="platform-tour-cta-ghost"
          >
            Meet Ghost Intelligence™
          </Link>
        </div>
      </div>
    </section>

    <EndPageCTA testId="platform-tour-end-cta" />
  </PublicLayout>
);

export default PlatformTourPage;
